/*
 * Page transition wrapper. The screen mounts inside this div keyed on
 * subPage, so React replaces the contents on navigation. data-direction
 * picks one of three keyframe animations.
 *
 * Slides are short (320ms) and use the same standard easing as the
 * scene-slide motion token so the live mode feels coherent.
 *
 * Layout: fills the live KioskStage's safe-area box (the stage reserves
 * --token-safe-* insets so page content never overlaps the edge chrome)
 * and centers its single child page. Pages size off --ui-scale and cap
 * their own width/height; flex-centering parks them in the middle of the
 * safe box so the bg frames them evenly.
 */
.pageTransition {
  animation-duration: 320ms;
  animation-timing-function: cubic-bezier(0.8, 0, 0.2, 1);
  animation-fill-mode: both;
  will-change: transform, opacity;
  /* In-flow flex child that fills the live stage's *content* box — i.e.
   * INSIDE the stage's --token-safe-* padding (an absolutely-positioned
   * inset:0 would have covered the padding too, defeating the safe area).
   * Centers whatever the page renders; pages keep their own internal
   * layout (grid for Feelings, flex column for Pledges, etc.). */
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
}

.pageTransition[data-direction='right'] {
  animation-name: slide-from-right;
}

.pageTransition[data-direction='left'] {
  animation-name: slide-from-left;
}

.pageTransition[data-direction='none'] {
  animation: none;
}

@keyframes slide-from-right {
  from {
    transform: translateX(48px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slide-from-left {
  from {
    transform: translateX(-48px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .pageTransition {
    animation: none;
  }
}

/*
 * Theme mascot. Two modes:
 *   - .mascotHome    full-size, bottom-left, used on the live Home page
 *                    (Sarah's v1 layout — toucan over the canopy floor)
 *   - .mascotCorner  smaller, perched in the upper-left corner of any
 *                    other live page so it stays visible without crowding
 *                    the calendar/day/weather/feelings/etc. content
 *
 * Both share the same layered drop-shadow so the mascot reads as
 * standing in front of the scene rather than pasted onto it.
 */
.mascotHome {
  position: fixed;
  left: clamp(24px, 4vw, 96px);
  /* Stand on the floor — ~10px from the bottom edge — so the mascot sits low
   * and its head clears the greeting/message card at the top. */
  bottom: 10px;
  pointer-events: none;
  z-index: 1;
}

.mascotHome .mascotImage {
  /* Scale with --ui-scale (keyed to viewport HEIGHT) like the rest of the UI,
   * NOT vw: a vw width balloons the mascot on wide/ultrawide screens (28vw hit
   * its 720px cap and the per-theme ×1.2 pushed it to ~860px). At --ui-scale
   * 1.0 (1080p) this is 420px and grows/shrinks proportionally for 720p…4K. */
  width: calc(420px * var(--ui-scale));
  height: auto;
}

/* On the 4–8 (JH) home the mascot is opt-in and shares the screen with the
 * To-Do/Turn-In boards, so shrink it ~25% (420 → 315) to crowd them less. */
.mascotHomeOlder.mascotHome .mascotImage {
  width: calc(315px * var(--ui-scale));
}

/* Per-theme floor alignment. Each mascot art carries a different amount of
 * empty margin below the character, so the shared bottom:10px leaves some
 * floating. translateY (measured per art from the image's lowest opaque row)
 * drops each one so its feet land ~10px off the floor; scale still bumps arts
 * that read small. Offsets are ×--ui-scale so the alignment holds from 720p to
 * 4K, and anchored bottom-left. Themes with feet already at the image bottom
 * (e.g. redeemer) need no rule and use the base above. */
.mascotHome .mascotImage[data-theme-key='forest'] {
  transform: translateY(calc(20px * var(--ui-scale)));
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='jungle'] {
  transform: translateY(calc(195px * var(--ui-scale))) scale(1.2);
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='beach'] {
  transform: translateY(calc(11px * var(--ui-scale))) scale(1.38);
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='ocean'] {
  transform: translateY(calc(72px * var(--ui-scale))) scale(1.2);
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='desert'] {
  transform: translateY(calc(13px * var(--ui-scale))) scale(1.2);
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='futuristic'] {
  transform: translateY(calc(37px * var(--ui-scale)));
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='space'] {
  transform: translateY(calc(19px * var(--ui-scale)));
  transform-origin: bottom left;
}

.mascotHome .mascotImage[data-theme-key='sarah'] {
  transform: translateY(calc(297px * var(--ui-scale))) scale(1.5625);
  transform-origin: bottom left;
}

.mascotCorner {
  position: fixed;
  left: clamp(12px, 2vw, 28px);
  top: clamp(8px, 1.5vh, 22px);
  pointer-events: none;
  z-index: 5;
}

/* Sized so the mascot's right edge stays inside the live safe-area's left
 * inset (--token-safe-x) at every viewport — it lives in the padded
 * corner margin and never overlaps the page content (e.g. the calendar's
 * month title). */
.mascotCorner .mascotImage {
  /* ~10% larger than the prior clamp(72, 6vw, 120). The right edge stays
   * inside --token-safe-x (~6.6vw left offset + width < the 9vw side inset). */
  width: clamp(79px, 6.6vw, 132px);
  height: auto;
}

.mascotImage {
  /* Block, not inline — an inline image carries a ~5px baseline descender gap
   * below it, which pushed the mascots' feet to ~15px instead of the intended
   * 10px off the bottom edge. */
  display: block;
  filter: drop-shadow(0 4px 8px rgba(8, 18, 12, 0.35))
    drop-shadow(0 18px 28px rgba(8, 18, 12, 0.32));
  user-select: none;
  -webkit-user-drag: none;
}
